import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackEvent } from '../utils/analytics';

const PRICES = [1200, 850, 450, 120, 0];
const WHATSAPP_LINK = "https://wa.me/393921037894?text=Ciao%2C%20vorrei%20sapere%20se%20il%20mio%20sgombero%20pu%C3%B2%20essere%20gratuito.%20Posso%20mandarvi%20qualche%20foto%3F";

export default function Psychology() {
  const [index, setIndex] = useState(0);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const interval = setInterval(() => {
        setIndex((prev) => {
          if (prev >= PRICES.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 800);
      return () => clearInterval(interval);
    }
  }, [isInView]);

  const currentPrice = PRICES[index];
  const isFinal = currentPrice === 0;

  return (
    <section ref={containerRef} className="py-24 md:py-40 bg-[#050505] relative overflow-hidden border-y border-white/5">
      {/* Background Glow */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] rounded-full blur-[120px] transition-colors duration-1000 pointer-events-none ${isFinal ? 'bg-maaich-lime/20' : 'bg-maaich-green/10'}`}></div>

      <div className="container mx-auto px-5 relative z-10 text-center">
        <motion.h2 
          className="text-5xl sm:text-7xl md:text-9xl font-display uppercase italic mb-8 leading-[0.85] text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          E SE NON DOVESSI <span className="text-maaich-lime block">PAGARE NIENTE?</span>
        </motion.h2>

        <motion.p 
          className="text-lg md:text-2xl text-white/70 max-w-3xl mx-auto font-body font-light mb-20 leading-relaxed"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          In alcuni casi gli oggetti che vuoi buttare possono coprire il costo dello sgombero. E ci sono situazioni in cui <span className="text-white font-semibold italic">non paghi nulla.</span>
        </motion.p>

        <div className="relative h-48 md:h-64 flex flex-col items-center justify-center mb-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPrice}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.1, y: -20 }}
              className={`relative text-[7rem] sm:text-[10rem] md:text-[14rem] font-display italic leading-none ${isFinal ? 'text-maaich-lime' : 'text-white'}`}
            >
              €{currentPrice}
              
              {/* Red Strikethrough Line - Only for non-zero prices */}
              {!isFinal && (
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: '110%' }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="absolute top-1/2 left-[-5%] h-3 md:h-5 bg-red-600 -rotate-12 origin-left shadow-[0_0_20px_rgba(220,38,38,0.5)]"
                />
              )}

              {/* Explosion Effect for Zero */}
              {isFinal && (
                <motion.div 
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 bg-maaich-lime rounded-full blur-3xl -z-10"
                />
              )}
            </motion.div>
          </AnimatePresence>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isFinal ? 1 : 0 }}
            className="mt-4"
          >
            <span className="font-display text-maaich-lime text-2xl uppercase italic tracking-widest">Sgombero Gratuito</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <a 
            href={WHATSAPP_LINK}
            target="_blank" 
            rel="noreferrer"
            onClick={() => trackEvent('whatsapp_click', 'sezione_gratis')}
            className="w-full sm:w-auto bg-maaich-lime text-maaich-black font-display text-2xl px-12 py-6 uppercase italic transition-all duration-300 flex items-center justify-center gap-3 hover:bg-white active:scale-95 shadow-2xl shadow-maaich-lime/20 group"
          >
            Scopri se il tuo è gratis
            <SafeIcon icon={FiIcons.FiArrowRight} className="text-3xl group-hover:translate-x-2 transition-transform" />
          </a>
          <p className="text-white/40 text-sm mt-6 font-body italic">
            Valutazione immediata basata sulle foto degli oggetti.
          </p>
        </motion.div>
      </div>
    </section>
  );
}